<!--
  Cambio cliente e/o auto della prenotazione, solo Simba
  (PATCH /booking/:id/customer-car).

  Il valet è legato all'AUTO: cambiandola, il valet consumato torna al suo
  pacchetto e, se la nuova auto ne ha uno valido, si consuma quello (come alla
  creazione). Tratte, driver e planner non cambiano.
-->
<template>
  <el-dialog
    :model-value="modelValue"
    title="Cambia cliente e auto"
    width="520px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="cca">
      <div class="cca__fld">
        <label>Cliente</label>
        <el-select
          v-model="customerId"
          placeholder="Cerca per cognome…"
          filterable
          remote
          reserve-keyword
          :remote-method="cerca"
          :loading="cercando"
          style="width: 100%"
          @change="onCliente"
        >
          <el-option
            v-for="c in clienti"
            :key="c.id"
            :label="etichetta(c)"
            :value="c.id"
          />
        </el-select>
      </div>
      <div class="cca__fld">
        <label>Auto</label>
        <el-select
          v-model="carId"
          placeholder="Nessuna auto"
          clearable
          :disabled="!customerId"
          :loading="caricandoAuto"
          style="width: 100%"
        >
          <el-option
            v-for="c in auto"
            :key="c.id"
            :label="[c.marca, c.modello, c.targa].filter(Boolean).join(' ')"
            :value="c.id"
          />
        </el-select>
        <p
          v-if="customerId && !caricandoAuto && !auto.length"
          class="cca__hint"
        >
          Il cliente non ha auto registrate.
        </p>
      </div>
      <template v-if="pickup">
        <el-checkbox v-model="usaValet">
          Usa il pacchetto valet dell'auto, se ne ha uno valido
        </el-checkbox>
        <p
          v-if="booking?.valet_consumed && autoCambiata"
          class="cca__hint"
        >
          Il valet consumato torna al pacchetto dell'auto di prima.
        </p>
      </template>
    </div>
    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">
        Annulla
      </el-button>
      <el-button
        type="primary"
        :disabled="!customerId || !cambiato"
        :loading="salvando"
        @click="salva"
      >
        Salva
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import bookingAPI from '../../services/bookingAPI.js';
import customerAPI from '../../services/customerAPI.js';
import carAPI from '../../services/carAPI.js';

export default {
  name: 'CustomerCarDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    booking: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const customerId = ref('');
    const carId = ref('');
    const usaValet = ref(true);
    const clienti = ref([]);
    const auto = ref([]);
    const cercando = ref(false);
    const caricandoAuto = ref(false);
    const salvando = ref(false);

    const pickup = computed(() =>
      ['pickup', 'pickup_loaner'].includes(props.booking?.type_prenotazione));
    const autoCambiata = computed(() => (carId.value || null) !== (props.booking?.car?.id || null));
    // Anche la sola spunta è una modifica: toglierla rende il valet al
    // pacchetto, metterla lo usa.
    const cambiato = computed(() => customerId.value !== props.booking?.customer?.id
      || autoCambiata.value
      || (pickup.value && usaValet.value !== !!props.booking?.valet_consumed));

    const etichetta = (c) => {
      const n = [c.name, c.surname].filter(Boolean).join(' ');
      return c.company_name ? `${c.company_name} (${n})` : n || c.mail;
    };

    const caricaAuto = async (id) => {
      auto.value = [];
      if (!id) return;
      caricandoAuto.value = true;
      try {
        const r = await carAPI.getCarsByCustomer(id);
        auto.value = r.data?.data ?? r.data ?? [];
      } catch (e) {
        console.warn('Auto del cliente non caricate:', e?.message);
      } finally {
        caricandoAuto.value = false;
      }
    };

    watch(() => props.modelValue, (open) => {
      if (!open || !props.booking) return;
      const c = props.booking.customer;
      clienti.value = c ? [c] : [];
      customerId.value = c?.id || '';
      carId.value = props.booking.car?.id || '';
      // La spunta parte da com'è la corsa adesso.
      usaValet.value = !!props.booking.valet_consumed;
      caricaAuto(customerId.value);
    }, { immediate: true });

    const cerca = async (q) => {
      if (!q || q.length < 2) return;
      cercando.value = true;
      try {
        const r = await customerAPI.getCustomersPaged({ search: q, limit: 20 });
        clienti.value = r.rows;
      } catch (e) {
        console.warn('Ricerca clienti fallita:', e?.message);
      } finally {
        cercando.value = false;
      }
    };

    // Con un'auto nuova si usa il suo pacchetto, come alla creazione.
    watch(carId, () => {
      if (autoCambiata.value) usaValet.value = true;
    });

    // Le auto sono del cliente: cambiato il cliente, quella di prima non vale.
    const onCliente = async (id) => {
      carId.value = '';
      await caricaAuto(id);
      if (auto.value.length === 1) carId.value = auto.value[0].id;
    };

    const salva = async () => {
      salvando.value = true;
      try {
        const r = await bookingAPI.changeCustomerCar(props.booking.id, {
          customerId: customerId.value,
          carId: carId.value || null,
          ...(pickup.value ? { usaValet: usaValet.value } : {}),
        });
        const v = r.data?.valet || {};
        let msg = 'Cliente e auto aggiornati.';
        if (v.prima && !v.dopo) msg += ' Il valet è tornato al pacchetto.';
        else if (!v.prima && v.dopo) msg += ' Usato il valet della nuova auto.';
        else if (v.prima && v.dopo && autoCambiata.value) msg += ' Usato il valet della nuova auto.';
        ElMessage.success(msg);
        emit('update:modelValue', false);
        emit('saved');
      } catch (e) {
        // il messaggio del server lo mostra l'interceptor di API.js
        console.error('Cambio cliente/auto non salvato:', e?.response?.status);
      } finally {
        salvando.value = false;
      }
    };

    return {
      customerId, carId, usaValet, clienti, auto, cercando, caricandoAuto, salvando,
      pickup, autoCambiata, cambiato, etichetta, cerca, onCliente, salva,
    };
  },
};
</script>

<style scoped>
.cca { display: flex; flex-direction: column; gap: 14px; }
.cca__fld label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.cca__hint { margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary); }
</style>
